import { useCallback, useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../API';
import LoginWidget from '../../widgets/LoginWidget';
import Tooltip from '../../components/Tooltip';
import ProjectItemListWithProgress from './ProjectItemListWithProgress';
import { WHEN_BELOW_STRICT } from './completionPolicy';
import { measureTopBarPaint, nextTopBarCompression } from './taskSectionLabelCollapse';
import styles from './ViewerTopBar.module.css';

/** Inline SVG empty checkbox; size/align via .completeBtnCheckIcon */
function CheckboxEmptyIcon() {
    return (
        <svg className={styles.completeBtnCheckIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
    );
}

/** Inline SVG checked checkbox; size/align via .completeBtnCheckIcon */
function CheckboxCheckedIcon() {
    return (
        <svg className={styles.completeBtnCheckIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M7 12.5l3.5 3.5 6.5-7" />
        </svg>
    );
}

/**
 * Universal topbar for the annotation viewer. Knows nothing about which annotation
 * UI is loaded. Task-specific controls are injected via the `taskControls` render prop.
 *
 * Layout (left → right):
 *   ← Project  |  Item n/N ‹ ›  |  [itemNavAccessory]  |  [itemMetadata left?]  |  [contextToolbar]  |  [taskAccessory]
 *   |  [taskSectionLabel] ⓘ [taskControls [taskUiSlot] [itemMetadata right?]]  |  Saved ✓  |  Complete ✓  |  Login
 *
 * Props:
 *   folderId         - project folder ID (for back link)
 *   projectName      - display name of the project
 *   items            - array of item docs in the project
 *   currentItem      - the currently active item doc
 *   onItemChange     - callback(newItem) when user clicks prev/next
 *   taskControls     - React node rendered in the task-controls slot
 *   instructions     - optional string shown in the ⓘ popover
 *   saveStatus       - 'idle' | 'saving' | 'saved' | 'error'
 *   saveError        - error message string when saveStatus === 'error'
 *   saveMode         - 'auto' | 'manual' | null
 *   hasUnsavedChanges - true when current item has unsaved annotation edits
 *   onManualSave     - optional callback for manual-save mode
 *   completable      - boolean — whether the Complete button is enabled (when not completed)
 *   completed        - boolean — whether this item is marked complete
 *   strictComplete   - whether the active UI considers strict requirements met (for confirm flow)
 *   incompleteReasons - string[] — human-readable hints when Complete is disabled
 *   completionPolicy - { whenBelowStrict, incompleteFinishMessage } from normalizeCompletionPolicy(uiConfig)
 *   taskSectionLabel - label before task controls (default 'Annotations:')
 *   onComplete       - callback(boolean) when completion is toggled
 *   progressMap      - optional { [itemId]: status } for dropdown badges
 *   onFinishedPersisted - optional callback() when PUT annotation/finished succeeds (e.g. refetch progress)
 *   getExportData    - optional () => object; returns current girderProjects.exportData snapshot
 *   backLinkTo       - optional; override Link "to" (e.g. for review viewer: progress page)
 *   backLinkLabel    - optional; override link text (e.g. "← Progress")
 *   showCompleteAndReset - optional; default true; when false, hide save indicator, reset, and complete (e.g. read-only review)
 *   showItemProgressInDropdown - optional; default true; when false, item dropdown does not show progress badges (e.g. review viewer)
 *   itemNavAccessory - optional React node rendered after item nav (e.g. shared ROI jump UI)
 *   contextToolbar - optional React node for viewer context that is not the annotation task (e.g. admin
 *     review overlay controls). Rendered after item nav / itemNavAccessory with a divider; separate from
 *     taskControls so task UIs and read-only review chrome stay semantically distinct.
 *   itemMetadata - optional React node for per-item metadata (filename-keyed CSV fields).
 *   itemMetadataPlacement - 'left' | 'right' (default 'right'). Left: after itemNavAccessory; right: inside taskControls after UI controls.
 *   taskAccessory - optional React node for shared task-related controls that are not owned by a single
 *     annotation UI module (e.g. Notes). Rendered between navigation/context chrome and the per-UI
 *     taskControls slot so the layout reads: [item nav | ROI nav] | [metadata] | [shared task chrome] | [UI controls].
 *   roiEditMode - optional; when true, hide Item finished (ROI editor; attestation is on project settings)
 *   onRevertToSaved - optional; reload annotation from server (discard unsaved edits). Segmentation wires this; omit otherwise.
 *   resetMenuClearSelected - optional; when true, show "Clear selected" in the Reset menu (metadata-driven).
 *   clearSelectedEnabled - optional; when resetMenuClearSelected, gates the Clear selected row (e.g. category + selection).
 */
export default function ViewerTopBar({
    folderId,
    projectName,
    items,
    currentItem,
    onItemChange,
    taskControls,
    instructions,
    saveStatus = 'idle',
    saveError = null,
    saveMode = null,
    hasUnsavedChanges = false,
    onManualSave = null,
    completable = false,
    completed = false,
    strictComplete = true,
    incompleteReasons = [],
    completionPolicy = { whenBelowStrict: WHEN_BELOW_STRICT.BLOCK, incompleteFinishMessage: '' },
    taskSectionLabel = 'Annotations:',
    taskSectionLabelMode = 'normal', // 'normal' | 'sr-only'
    onComplete,
    resettable = false,
    onReset,
    onRevertToSaved = null,
    hasSelection = false,
    onDeleteSelected,
    resetMenuClearSelected = false,
    clearSelectedEnabled = false,
    validationMessage = null,
    progressMap = {},
    onFinishedPersisted = () => {},
    getExportData,
    backLinkTo,
    backLinkLabel,
    showCompleteAndReset = true,
    showItemProgressInDropdown = true,
    showViewerDeleteButton = true,
    itemNavAccessory = null,
    itemMetadata = null,
    itemMetadataPlacement = 'right',
    contextToolbar = null,
    taskAccessory = null,
    roiEditMode = false,
}) {
    // ── Item panel state ───────────────────────────────────────────────────
    // pinned = stays open until outside click, Escape, or trigger click
    // hovered = open transiently while mouse is inside the unified container
    const [itemPanelPinned, setItemPanelPinned] = useState(false);
    const [itemPanelHovered, setItemPanelHovered] = useState(false);
    const showItemPanel = itemPanelPinned || itemPanelHovered;
    const itemNavContainerRef = useRef(null);
    const hoverCloseTimerRef = useRef(null);

    // ── Instructions popover state ─────────────────────────────────────────
    // pinned = stays open until clicked again; visible = shown transiently on hover
    const [instructionsPinned, setInstructionsPinned] = useState(false);
    const [instructionsHovered, setInstructionsHovered] = useState(false);
    const showInstructions = instructionsPinned || instructionsHovered;

    // ── Reset menu (clear all vs revert) ───────────────────────────────────
    const [resetMenuOpen, setResetMenuOpen] = useState(false);
    const resetMenuWrapRef = useRef(null);

    // ── Mark finished while below strict completeness (completionPolicy.whenBelowStrict === 'confirm')
    const [confirmIncompleteFinish, setConfirmIncompleteFinish] = useState(false);

    // ── Validation message (e.g. bounding box rejected): auto-clear after 5s
    const [localValidationMessage, setLocalValidationMessage] = useState(null);
    const topBarRef = useRef(null);
    const taskSectionRef = useRef(null);
    const taskControlsRef = useRef(null);
    const taskLabelRef = useRef(null);
    const preTaskSectionRef = useRef(null);
    const rightClusterRef = useRef(null);
    const labelWidthCacheRef = useRef(0);
    const taskLabelCollapsedRef = useRef(false);
    const taskRowWrappedRef = useRef(false);
    const wrapTriggerWidthRef = useRef(null);
    const [taskLabelCollapsed, setTaskLabelCollapsed] = useState(false);
    const [taskRowWrapped, setTaskRowWrapped] = useState(false);
    useEffect(() => {
        setLocalValidationMessage(validationMessage ?? null);
        if (!validationMessage) return;
        const t = setTimeout(() => setLocalValidationMessage(null), 5000);
        return () => clearTimeout(t);
    }, [validationMessage]);

    // Compression from painted bounds: label hide when it would crowd, row 2 on paint overlap.
    useEffect(() => {
        const topBar = topBarRef.current;
        const section = taskSectionRef.current;
        const rightCluster = rightClusterRef.current;
        if (!topBar || !section || !rightCluster) return undefined;
        if (typeof ResizeObserver === 'undefined') return undefined;

        let raf = 0;
        const measure = () => {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                const labelEl = taskLabelRef.current;
                const controls = taskControlsRef.current;
                if (labelEl && labelEl.offsetWidth > 2) {
                    labelWidthCacheRef.current = labelEl.offsetWidth;
                }
                const labelWidth = labelWidthCacheRef.current;

                const paint = measureTopBarPaint({
                    section,
                    controls,
                    rightCluster,
                    topBar,
                });

                const next = nextTopBarCompression({
                    labelCollapsed: taskLabelCollapsedRef.current,
                    taskRowWrapped: taskRowWrappedRef.current,
                }, {
                    ...paint,
                    labelWidth,
                    wrapTriggerWidth: wrapTriggerWidthRef.current,
                    taskSectionLabelMode,
                });

                const prevLabelCollapsed = taskLabelCollapsedRef.current;
                const prevTaskRowWrapped = taskRowWrappedRef.current;

                taskLabelCollapsedRef.current = next.labelCollapsed;
                taskRowWrappedRef.current = next.taskRowWrapped;
                wrapTriggerWidthRef.current = next.wrapTriggerWidth;
                if (next.labelCollapsed !== prevLabelCollapsed) {
                    setTaskLabelCollapsed(next.labelCollapsed);
                }
                if (next.taskRowWrapped !== prevTaskRowWrapped) {
                    setTaskRowWrapped(next.taskRowWrapped);
                }
            });
        };

        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(topBar);
        ro.observe(section);
        ro.observe(rightCluster);
        if (taskControlsRef.current) {
            ro.observe(taskControlsRef.current);
        }
        return () => {
            ro.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [taskSectionLabel, taskSectionLabelMode, taskControls, itemMetadata, showCompleteAndReset]);

    useEffect(() => {
        if (!taskControls) {
            taskLabelCollapsedRef.current = false;
            taskRowWrappedRef.current = false;
            wrapTriggerWidthRef.current = null;
            setTaskLabelCollapsed(false);
            setTaskRowWrapped(false);
        }
    }, [taskControls]);

    const currentIndex = items.findIndex((it) => String(it._id) === String(currentItem?._id));
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < items.length - 1;

    function goPrev() { if (hasPrev) onItemChange(items[currentIndex - 1]); }
    function goNext() { if (hasNext) onItemChange(items[currentIndex + 1]); }

    // ── Item panel handlers ────────────────────────────────────────────────
    // Use a close-on-delay pattern: mouseleave starts a short timer before
    // hiding the panel; mouseenter cancels it. This tolerates the brief moment
    // when the mouse crosses between the trigger and the absolutely-positioned
    // panel (which can fire a leave on the container in some browsers/speeds).
    function handleContainerMouseEnter() {
        clearTimeout(hoverCloseTimerRef.current);
        setItemPanelHovered(true);
    }
    function handleContainerMouseLeave() {
        hoverCloseTimerRef.current = setTimeout(() => setItemPanelHovered(false), 120);
    }
    function handleNavClick() {
        setItemPanelPinned((p) => !p);
    }
    function closeItemPanel() {
        setItemPanelPinned(false);
        setItemPanelHovered(false);
    }

    // Escape key closes a pinned panel
    useEffect(() => {
        if (!itemPanelPinned) return;
        function onKeyDown(e) { if (e.key === 'Escape') closeItemPanel(); }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [itemPanelPinned]);

    // Outside-click closes a pinned panel
    useEffect(() => {
        if (!itemPanelPinned) return;
        function onMouseDown(e) {
            if (itemNavContainerRef.current && !itemNavContainerRef.current.contains(e.target)) {
                closeItemPanel();
            }
        }
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [itemPanelPinned]);

    // ── Instructions button handlers ───────────────────────────────────────
    function handleInstructionsMouseEnter() {
        if (!instructionsPinned) setInstructionsHovered(true);
    }
    function handleInstructionsMouseLeave() {
        setInstructionsHovered(false);
    }
    function handleInstructionsClick() {
        if (instructionsPinned) {
            setInstructionsPinned(false);
            setInstructionsHovered(false);
        } else {
            setInstructionsPinned(true);
            setInstructionsHovered(false);
        }
    }

    function persistFinishedStatus(nextCompleted) {
        if (!folderId || !currentItem?._id) return;
        const exportData = getExportData?.();
        api.put(
            `projects/${folderId}/items/${currentItem._id}/annotation/finished`,
            { json: { status: nextCompleted ? 'completed' : 'in_progress', girderProjects: exportData ? { exportData } : undefined } },
        ).then((res) => {
            // eslint-disable-next-line no-console
            console.log('[ViewerTopBar] finished flag updated', {
                itemId: currentItem._id,
                nextStatus: nextCompleted ? 'completed' : 'in_progress',
                httpStatus: res.status,
            });
            onFinishedPersisted();
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.error('[ViewerTopBar] failed to update finished flag', err);
        });
    }

    /** Complete button: unmark is immediate; mark may require incomplete confirmation. */
    function handleCompleteClick() {
        const next = !completed;
        if (completed) {
            if (onComplete) onComplete(next);
            persistFinishedStatus(next);
            return;
        }
        if (!completable) return;
        if (
            completionPolicy?.whenBelowStrict === WHEN_BELOW_STRICT.CONFIRM
            && !strictComplete
        ) {
            setConfirmIncompleteFinish(true);
            return;
        }
        if (onComplete) onComplete(next);
        persistFinishedStatus(true);
    }

    function handleConfirmIncompleteFinishYes() {
        setConfirmIncompleteFinish(false);
        if (onComplete) onComplete(true);
        persistFinishedStatus(true);
    }

    function handleConfirmIncompleteFinishNo() {
        setConfirmIncompleteFinish(false);
    }

    useEffect(() => {
        if (!confirmIncompleteFinish) return;
        function onKeyDown(e) {
            if (e.key === 'Escape') setConfirmIncompleteFinish(false);
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [confirmIncompleteFinish]);

    // ── Reset menu handlers ───────────────────────────────────────────────
    const closeResetMenu = useCallback(() => {
        setResetMenuOpen(false);
    }, []);
    function toggleResetMenu() {
        setResetMenuOpen((o) => !o);
    }
    function handleResetMenuClearAll() {
        closeResetMenu();
        if (onReset) onReset();
    }
    function handleResetMenuRevert() {
        closeResetMenu();
        if (onRevertToSaved) onRevertToSaved();
    }
    function handleResetMenuClearSelected() {
        closeResetMenu();
        if (onDeleteSelected) onDeleteSelected();
    }

    const resetMenuTriggerEnabled =
        Boolean(onReset)
        && (resettable
            || (hasUnsavedChanges && onRevertToSaved)
            || (resetMenuClearSelected && clearSelectedEnabled && onDeleteSelected));

    useEffect(() => {
        if (!resetMenuOpen) return undefined;
        function onKeyDown(e) {
            if (e.key === 'Escape') closeResetMenu();
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [resetMenuOpen, closeResetMenu]);

    useEffect(() => {
        if (!resetMenuOpen) return undefined;
        function onMouseDown(e) {
            if (resetMenuWrapRef.current && !resetMenuWrapRef.current.contains(e.target)) {
                closeResetMenu();
            }
        }
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [resetMenuOpen, closeResetMenu]);

    // ── Save indicator ─────────────────────────────────────────────────────
    const saveIndicator = (() => {
        if (saveStatus === 'saving') return <span className={styles.savingIndicator}>Saving…</span>;
        if (saveStatus === 'saved')  return <span className={styles.savedIndicator}>Saved ✓</span>;
        if (saveStatus === 'error')  return (
            <span className={styles.saveErrorIndicator} title={saveError}>Save failed ✗</span>
        );
        if (saveMode === 'manual' && hasUnsavedChanges) {
            return <span className={styles.unsavedIndicator}>Unsaved changes</span>;
        }
        return null;
    })();

    const manualSaveArea = (() => {
        if (saveMode !== 'manual' || typeof onManualSave !== 'function') return null;
        const saveDisabled = !hasUnsavedChanges || saveStatus === 'saving';
        const savePrimary = hasUnsavedChanges && saveStatus !== 'saving';
        return (
            <button
                type="button"
                className={`${styles.manualSaveBtn} ${savePrimary ? styles.manualSaveBtnPrimary : ''}`.trim()}
                disabled={saveDisabled}
                onClick={() => onManualSave()}
                title={!hasUnsavedChanges ? 'No unsaved changes.' : 'Save annotation changes'}
            >
                Save
            </button>
        );
    })();

    const savePersistCluster =
        saveIndicator || manualSaveArea ? (
            <div className={styles.savePersistCluster}>
                {saveIndicator}
                {manualSaveArea}
            </div>
        ) : null;

    // ── Complete button area ───────────────────────────────────────────────
    const completeDisabledHint =
        incompleteReasons.length > 0
            ? incompleteReasons.join(' ')
            : 'Finish required annotations before marking this item as finished.';
    const completeTooltipText = completed
        ? 'Click to un-mark as finished'
        : completable
            ? 'Mark this item as finished'
            : completeDisabledHint;
    const completeArea = (() => {
        if (completed) {
            return (
                <Tooltip text={completeTooltipText} position="bottom">
                    <button
                        type="button"
                        className={`${styles.completeBtn} ${styles.completeBtnDone}`}
                        onClick={handleCompleteClick}
                        aria-label={completeTooltipText}
                    >
                        Item finished<span className={styles.invisibleQuestion} aria-hidden="true">? </span>
                        <CheckboxCheckedIcon />
                    </button>
                </Tooltip>
            );
        }
        return (
            <Tooltip text={completeTooltipText} position="bottom">
                <button
                    type="button"
                    className={styles.completeBtn}
                    disabled={!completable}
                    onClick={handleCompleteClick}
                    aria-label={completeTooltipText}
                >
                    Item finished? <CheckboxEmptyIcon />
                </button>
            </Tooltip>
        );
    })();

    // ── Reset menu: clear all (local + dirty) vs revert to server ──────────
    const resetArea = (() => {
        if (!onReset) return null;
        return (
            <div className={styles.resetMenuWrap} ref={resetMenuWrapRef}>
                <button
                    type="button"
                    className={styles.resetBtn}
                    disabled={!resetMenuTriggerEnabled}
                    onClick={toggleResetMenu}
                    aria-expanded={resetMenuOpen}
                    aria-haspopup="true"
                    title={
                        resetMenuTriggerEnabled
                            ? 'Clear all, clear selected, or discard unsaved changes'
                            : 'Nothing to reset'
                    }
                >
                    Reset…
                </button>
                {resetMenuOpen && (
                    <div className={styles.resetMenuPopover} role="presentation">
                        <button
                            type="button"
                            className={styles.resetMenuAction}
                            disabled={!resettable}
                            onClick={handleResetMenuClearAll}
                            title={resettable ? 'Remove all regions on this slide' : 'No regions to remove'}
                            aria-label={resettable ? 'Clear all regions' : 'Clear all, disabled: no regions'}
                        >
                            Clear all
                        </button>
                        {resetMenuClearSelected && onDeleteSelected ? (
                            <button
                                type="button"
                                className={`${styles.resetMenuAction} ${styles.resetMenuActionBorderTop}`.trim()}
                                disabled={!clearSelectedEnabled}
                                onClick={handleResetMenuClearSelected}
                                title={
                                    clearSelectedEnabled
                                        ? 'Remove selected regions from the slide'
                                        : 'Choose a category and select regions first'
                                }
                                aria-label={
                                    clearSelectedEnabled
                                        ? 'Clear selected regions'
                                        : 'Clear selected, disabled'
                                }
                            >
                                Clear selected
                            </button>
                        ) : null}
                        {onRevertToSaved ? (
                            <Tooltip
                                text={
                                    hasUnsavedChanges
                                        ? 'Reload the last saved annotation from the server and discard unsaved edits.'
                                        : 'No unsaved changes to discard'
                                }
                                position="bottom"
                            >
                                <span className={styles.resetMenuTooltipAnchor}>
                                    <button
                                        type="button"
                                        className={`${styles.resetMenuAction} ${styles.resetMenuActionBorderTop}`.trim()}
                                        disabled={!hasUnsavedChanges}
                                        onClick={handleResetMenuRevert}
                                        aria-label={
                                            hasUnsavedChanges
                                                ? 'Discard changes: reload last saved annotation from the server'
                                                : 'Discard changes, disabled: nothing to discard'
                                        }
                                    >
                                        Discard changes
                                    </button>
                                </span>
                            </Tooltip>
                        ) : null}
                    </div>
                )}
            </div>
        );
    })();

    // ── Delete selected button; hidden when UI provides its own (e.g. BoundingBox selection bar)
    const deleteSelectedArea = (() => {
        if (
            !showViewerDeleteButton
            || resetMenuClearSelected
            || !onDeleteSelected
            || !hasSelection
        ) {
            return null;
        }
        return (
            <Tooltip text="Delete selected" position="bottom">
                <button
                    type="button"
                    className={styles.deleteSelectedBtn}
                    onClick={onDeleteSelected}
                    title="Delete selected"
                    aria-label="Delete selected"
                >
                    🗑
                </button>
            </Tooltip>
        );
    })();

    const metadataOnLeft = itemMetadataPlacement === 'left';
    const itemMetadataInTaskControls = !metadataOnLeft && itemMetadata ? (
        <>
            <div className={styles.divider} />
            <div className={styles.itemMetadata}>{itemMetadata}</div>
        </>
    ) : null;
    const itemMetadataLeftSlot = metadataOnLeft && itemMetadata ? (
        <>
            <div className={styles.divider} />
            <div className={styles.itemMetadata}>{itemMetadata}</div>
        </>
    ) : null;

    return (
        <div
            ref={topBarRef}
            className={[
                styles.topBar,
                taskRowWrapped ? styles.topBarTaskRowWrapped : '',
            ].filter(Boolean).join(' ')}
        >

            {/* ── Back link ── */}
            <Link to={backLinkTo ?? `/projects/${folderId}`} className={styles.backLink}>
                {backLinkLabel ?? `← ${projectName || 'Project'}`}
            </Link>

            <div className={styles.divider} />

            {/* ── Item navigator: ‹ n/N · Item Name › (with item picker dropdown) ── */}
            {/* Single container owns all mouse enter/leave so moving from trigger
                into the panel never fires a leave — fixing the "gap disappear" bug. */}
            <div
                ref={itemNavContainerRef}
                className={styles.itemNav}
                onMouseEnter={handleContainerMouseEnter}
                onMouseLeave={handleContainerMouseLeave}
            >
                <button
                    type="button"
                    className={styles.navArrow}
                    onClick={goPrev}
                    disabled={!hasPrev}
                    aria-label="Previous item"
                >
                    ‹
                </button>

                {/* Clickable trigger: counter + item name */}
                <div
                    className={`${styles.itemNavTrigger} ${itemPanelPinned ? styles.itemNavTriggerPinned : ''}`}
                    onClick={handleNavClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNavClick(); }}
                    aria-haspopup="listbox"
                    aria-expanded={showItemPanel}
                >
                    <span className={styles.itemCount}>
                        {currentIndex + 1}/{items.length}
                    </span>
                    <span className={styles.itemName} title={currentItem?.name}>
                        {currentItem?.name ?? '—'}
                    </span>
                </div>

                <button
                    type="button"
                    className={styles.navArrow}
                    onClick={goNext}
                    disabled={!hasNext}
                    aria-label="Next item"
                >
                    ›
                </button>

                {/* Item picker dropdown panel — no gap between trigger and panel */}
                {showItemPanel && (
                    <div className={styles.itemPanel} role="listbox">
                        <ProjectItemListWithProgress
                            items={items}
                            progressMap={progressMap}
                            currentItemId={currentItem?._id}
                            onSelectItem={(item) => { closeItemPanel(); onItemChange(item); }}
                            showProgressBadge={showItemProgressInDropdown}
                        />
                    </div>
                )}
            </div>

            {itemNavAccessory ? (
                <div className={styles.itemNavAccessory}>{itemNavAccessory}</div>
            ) : null}

            {itemMetadataLeftSlot}

            {contextToolbar ? (
                <>
                    <div className={styles.divider} />
                    <div className={styles.contextToolbar}>{contextToolbar}</div>
                </>
            ) : null}

            {taskAccessory ? (
                <>
                    <div className={styles.divider} />
                    <div className={styles.taskAccessory}>{taskAccessory}</div>
                </>
            ) : null}

            {taskControls && <div ref={preTaskSectionRef} className={styles.divider} />}

            {/* ── Measurements label + ⓘ + task controls ── */}
            {taskControls && (
                <div className={styles.taskSection} ref={taskSectionRef}>
                    {/* Label and instructions button grouped together */}
                    <span
                        ref={taskLabelRef}
                        className={[
                            styles.taskLabel,
                            (taskLabelCollapsed || taskSectionLabelMode === 'sr-only') ? styles.taskLabelCollapsed : '',
                        ].filter(Boolean).join(' ')}
                    >
                        {taskSectionLabel}
                    </span>
                    {instructions && (
                        <div className={styles.instructionsWrapper}>
                            <button
                                type="button"
                                className={[
                                    styles.instructionsBtn,
                                    instructionsPinned ? styles.instructionsBtnPinned : '',
                                ].filter(Boolean).join(' ')}
                                onMouseEnter={handleInstructionsMouseEnter}
                                onMouseLeave={handleInstructionsMouseLeave}
                                onClick={handleInstructionsClick}
                                aria-label={instructionsPinned ? 'Hide instructions' : 'Show instructions'}
                                aria-expanded={showInstructions}
                            >
                                ⓘ
                            </button>
                            {showInstructions && (
                                <div className={styles.instructionsPopover} role="tooltip">
                                    {instructionsPinned && (
                                        <button
                                            type="button"
                                            className={styles.instructionsClose}
                                            onClick={() => { setInstructionsPinned(false); setInstructionsHovered(false); }}
                                            aria-label="Close instructions"
                                        >
                                            ✕
                                        </button>
                                    )}
                                    <div className={styles.instructionsText}>{instructions}</div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* The UI-specific slot buttons */}
                    <div className={styles.taskControls} ref={taskControlsRef}>
                        <div className={styles.taskUiSlot}>
                            {taskControls}
                        </div>
                        {itemMetadataInTaskControls}
                    </div>
                    {localValidationMessage && (
                        <div className={styles.validationMessage} role="alert">
                            {localValidationMessage}
                        </div>
                    )}
                </div>
            )}

            <div ref={rightClusterRef} className={styles.rightCluster}>
                {/* ── Save status, Reset, Complete (hidden in review/read-only mode) ── */}
                {showCompleteAndReset && (
                    <>
                        {/* aria-live omitted: parent re-renders would spam screen readers */}
                        {savePersistCluster}
                        {resetArea}
                        {deleteSelectedArea}
                        {!roiEditMode && completeArea}
                    </>
                )}

                <div className={styles.divider} />

                {/* ── Login widget ── */}
                <div className={styles.loginWidget}>
                    <LoginWidget buttonClassName="viewerLoginBtn" />
                </div>
            </div>

            {confirmIncompleteFinish && (
                <div
                    className={styles.finishConfirmBackdrop}
                    role="presentation"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) handleConfirmIncompleteFinishNo();
                    }}
                >
                    <div
                        className={styles.finishConfirmDialog}
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="finish-confirm-title"
                        aria-describedby="finish-confirm-body"
                    >
                        <div id="finish-confirm-title" className={styles.finishConfirmTitle}>
                            Mark as finished?
                        </div>
                        <div id="finish-confirm-body" className={styles.finishConfirmBody}>
                            {completionPolicy?.incompleteFinishMessage}
                        </div>
                        <div className={styles.finishConfirmActions}>
                            <button type="button" className={styles.confirmCancel} onClick={handleConfirmIncompleteFinishNo}>
                                Cancel
                            </button>
                            <button type="button" className={styles.confirmYes} onClick={handleConfirmIncompleteFinishYes}>
                                Mark finished
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
